import Link from "next/link";

import cn from "classnames";

import { CarouselCentered } from "@/components/carouselCentered/carouselCentered.component";
import { PackageCard } from "@/components/packageCard/packageCard.component";
import { Text } from "@/components/text/text.component";

import { brown } from "@/app/font";
import type { GetPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { PromoBadge } from "../promoBadge/promoBadge.component";
import { RichText } from "../richText/richText.component";
import { FeaturedKitLink } from "./featuredKitLink.component";

export const FeaturedKits = ({ block }: FeaturedKitsProps) => {
  const packages = block.packages ?? [];

  if (packages.length === 0) return null;

  function renderDesktopGrid(packages: FeaturedKitPackage[]) {
    return (
      <div
        data-testid="featured-kits-desktop-grid"
        className="container mx-auto hidden px-4 lg:block">
        <div className="grid grid-cols-3 gap-6">
          {packages.map((pkg, index) => renderPackageCard(pkg, index))}
        </div>
        {block.ctaLabel && block.ctaLink?.url && (
          <div className="mt-8 flex justify-center">
            <Link
              href={block.ctaLink.url}
              className={cn(
                brown.className,
                "border-secondary text-secondary hover:bg-secondary focus-visible:ring-secondary inline-flex min-h-12 items-center justify-center rounded-full border-2 px-8 py-3 text-sm font-bold uppercase transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              )}>
              {block.ctaLabel}
            </Link>
          </div>
        )}
      </div>
    );
  }

  function renderDesktopCarousel(packages: FeaturedKitPackage[]) {
    return (
      <CarouselCentered
        className="container mx-auto hidden px-4 lg:block"
        data-testid="featured-kits-desktop-carousel"
        ctaLabel={block.ctaLabel ?? "Shop All Kits"}
        ctaHref={block.ctaLink?.url ?? undefined}
        ctaClassName={brown.className}
        swiperItemsClassName="self-stretch"
        slideInnerClassName="h-full"
        desktopSlidesPerView={3}
        desktopSpaceBetween={24}
        centeredSlides={false}
        initialSlide={0}>
        {packages.map((pkg, index) => renderPackageCard(pkg, index))}
      </CarouselCentered>
    );
  }

  function renderMobileCarousel(packages: FeaturedKitPackage[]) {
    return (
      <CarouselCentered
        className="container mx-auto lg:hidden"
        ctaLabel={block.ctaLabel ?? "Shop All Kits"}
        ctaHref={block.ctaLink?.url ?? undefined}
        ctaClassName={brown.className}
        swiperItemsClassName="self-stretch"
        slideInnerClassName="h-full">
        {packages.map((pkg, index) => renderPackageCard(pkg, index))}
      </CarouselCentered>
    );
  }

  function renderPackageCard(item: FeaturedKitPackage, index: number) {
    const { product } = item;
    if (!product) return null;
    const imageUrl = item.image?.asset
      ? urlFor(item.image).width(540).height(540).url()
      : product.featuredImage?.image
        ? urlFor(product.featuredImage.image).width(540).height(540).url()
        : undefined;
    const imageAlt =
      item.image?.alt ?? product.featuredImage?.alt ?? product.name;

    const productHref = product.slug ?? "#";

    return (
      <FeaturedKitLink
        key={product._id}
        pkg={item}
        index={index}
        href={productHref}
        className="focus-visible:ring-secondary my-1 block h-full rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none">
        <PackageCard
          className="min-h-120 w-full md:h-full lg:h-120"
          title={product.name}
          price={product.listPrice ?? 0}
          description={product.shortDescription ?? undefined}
          image={imageUrl}
          imageAlt={imageAlt}
          chips={item.chips}
          href={productHref}
        />
      </FeaturedKitLink>
    );
  }

  return (
    <section
      data-testid="featured-kits"
      className="flex w-full flex-col items-center justify-center gap-8 py-14">
      <div className="flex flex-col gap-6">
        <div className="px-4 text-center">
          {block.sectionLabel && (
            <Text
              variant="h5"
              component="p"
              className="text-greyscale-headline uppercase">
              {block.sectionLabel}
            </Text>
          )}
          {block.title && (
            <Text
              variant="h3"
              component="h2"
              className="text-greyscale-headline mb-4">
              {block.title}
            </Text>
          )}
          {block.description && <RichText>{block.description}</RichText>}
        </div>
        <div className="px-4">
          {block.promotionCallout && (
            <PromoBadge
              label={block.promotionCallout}
              testId="featured-kits-promo-badge"
              iconSize={16}
              centered
            />
          )}
        </div>
      </div>

      {packages.length > 3
        ? renderDesktopCarousel(packages)
        : renderDesktopGrid(packages)}
      {renderMobileCarousel(packages)}
    </section>
  );
};

type PageBuilderBlock = NonNullable<
  NonNullable<GetPageQueryResult>["pageBuilder"]
>[number];

export type FeaturedKitsBlock = Extract<
  PageBuilderBlock,
  { _type: "featuredKits" }
>;

export type FeaturedKitPackage = NonNullable<
  FeaturedKitsBlock["packages"]
>[number];

export type FeaturedKitProduct = FeaturedKitPackage["product"];

export type FeaturedKitsProps = {
  block: FeaturedKitsBlock;
};
